import { Component, OnInit, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ObjektStrukturService, RoomIndikator } from '../../../services/objekt-struktur.service';
import { IndikatorenService } from '../../../indikatoren/services/indikatoren.service';
import { Indikator } from '../../../indikatoren/interfaces/indikatoren.interface';
import { Observable } from 'rxjs';

export interface EditIndicatorData {
  id: string;
  roomId: string;
  standortId: string;
  currentData: RoomIndikator;
  // Pre-loaded settings data
  indikatorOptions: any[];
}

@Component({
  selector: 'app-edit-indicator-dialog',
  templateUrl: './edit-indicator-dialog.component.html',
  styleUrls: ['./edit-indicator-dialog.component.css'],
})
export class EditIndicatorDialogComponent implements OnInit {
  frmIndicator: UntypedFormGroup;
  indicatorId: string;
  roomId: string;
  standortId: string;
  
  // Settings dropdowns
  indikatorOptions: any[] = [];
  indikatoren: Indikator[] = [];

  loading = false;

  constructor(
    public dialogRef: MatDialogRef<EditIndicatorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditIndicatorData,
    private _fb: UntypedFormBuilder,
    private objektStrukturService: ObjektStrukturService,
    private indikatorenService: IndikatorenService
  ) {
    // Remove 'ind_' prefix from the ID for the API call
    this.indicatorId = data.id.startsWith('ind_') ? data.id.substring(4) : data.id;
    this.standortId = data.standortId;

    // Get settings data from dialog data
    this.indikatorOptions = data.indikatorOptions || [];

    console.log('Edit dialog data:', data);
    console.log('Current data:', data.currentData);
    console.log('Original ID:', data.id);
    console.log('Processed indicatorId:', this.indicatorId);
  }

  async ngOnInit() {
    await this.createForm();
    this.loadIndikatoren();
  }

  async createForm() {
    // Handle the data structure - it might be nested under 'data' property
    let currentData = this.data.currentData;
    if (currentData && (currentData as any).data) {
      currentData = (currentData as any).data;
    }
    
    // Get roomId from currentData if not provided in data
    const roomId = this.data.roomId || (currentData as any)?.room_id;
    
    // Convert status to number (database stores as tinyint(1))
    const statusValue = (currentData as any)?.status;
    const status = statusValue !== undefined ? Number(statusValue) : 1;
    
    // Handle null/undefined values for required fields
    const schwellenwert = (currentData as any)?.schwellenwert;
    const schwellenwertValue = schwellenwert !== null && schwellenwert !== undefined ? schwellenwert : '';
    
    // Handle indikator_number - use existing or generate new one
    const existingNumber = (currentData as any)?.indikator_number;
    const rawNumber = existingNumber || await this.generateNewIndikatorNumber();
    const indikatorNumber = rawNumber ? rawNumber.toString().padStart(3, '0') : '001';

    this.frmIndicator = this._fb.group({
      room_id: [roomId],
      standort_id: [this.standortId],
      indikator_id: [(currentData as any)?.indikator_id || '', [Validators.required]],
      name: [(currentData as any)?.name || '', [Validators.required]],
      indikator_number: [{value: indikatorNumber, disabled: true}], // Disabled field - cannot be changed
      einheit: [(currentData as any)?.einheit || '', [Validators.required]],
      schwellenwert: [schwellenwertValue, [Validators.required]],
      bemerkung: [(currentData as any)?.bemerkung || ''],
      status: [status, [Validators.required]]
    });

    console.log('Form created with values:', this.frmIndicator.value);
    console.log('Current data extracted:', currentData);
    console.log('Room ID:', roomId);
    console.log('Schwellenwert original:', schwellenwert);
    console.log('Schwellenwert processed:', schwellenwertValue);
    console.log('Form valid:', this.frmIndicator.valid);
    console.log('Form errors:', this.frmIndicator.errors);
    
    // Log individual field errors
    Object.keys(this.frmIndicator.controls).forEach(key => {
      const control = this.frmIndicator.get(key);
      if (control && control.errors) {
        console.log(`Field ${key} errors:`, control.errors);
      }
    });
  }

  loadIndikatoren() {
    this.loading = true;
    this.indikatorenService.getIndikatoren(1, 100, '', 'status_id', 'desc').subscribe({
      next: (response) => {
        // Load all indikatoren (both active and inactive for editing)
        this.indikatoren = response.data || [];
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading indikatoren:', error);
        this.loading = false;
      }
    });
  }

  async generateNewIndikatorNumber(): Promise<number> {
    try {
      // Get the highest indikator_number for this standort
      const response = await this.objektStrukturService.getMaxIndikatorNumber(this.standortId).toPromise();
      return (response?.maxNumber || 0) + 1;
    } catch (error) {
      console.error('Error generating indikator number:', error);
      // Fallback: return a timestamp-based number
      return Date.now() % 10000;
    }
  }

  onConfirm(): void {
    console.log('onConfirm called');
    console.log('Form valid:', this.frmIndicator.valid);
    console.log('Form value:', this.frmIndicator.value);
    
    // Log all field errors
    Object.keys(this.frmIndicator.controls).forEach(key => {
      const control = this.frmIndicator.get(key);
      if (control && control.errors) {
        console.log(`Field ${key} errors:`, control.errors);
        console.log(`Field ${key} value:`, control.value);
      }
    });
    
    if (this.frmIndicator.valid) {
      this.loading = true;
      const updateData = this.frmIndicator.value;
      
      // Ensure status is a number (0 or 1)
      updateData.status = Number(updateData.status);
      
      // If status is being set to inactive (0), set updated_at
      if (updateData.status === 0) {
        updateData.updated_at = new Date().toISOString();
      }
      
      console.log('Updating room-indikator with data:', updateData);
      
      this.objektStrukturService.updateRoomIndikator(this.indicatorId, updateData)
        .subscribe({
          next: (response) => {
            this.loading = false;
            this.dialogRef.close({ 
              event: 'update', 
              data: {
                id: this.indicatorId,
                ...updateData
              }
            });
          },
          error: (error) => {
            this.loading = false;
            console.error('Error updating indicator:', error);
            // You could show an error message here
          }
        });
    } else {
      console.log('Form is not valid, cannot save');
    }
  }


  onDismiss(): void {
    this.dialogRef.close({ event: 'cancel' });
  }
}
